<template>
  <div class="table-container">
    <table class="content-table">
      <thead class="header-table">
      <tr class="trhead">
        <th><input type="checkbox" :checked="selectAll" @change="toggleAll"></th>
        <th>{{ $t('product') }}</th>
        <th>{{ $t('status_payment_title') }}</th>
        <th>{{ $t('added_date') }}</th>
        <th>{{ $t('price') }}</th>
        <th>{{ $t('category') }}</th>
        <th>{{ $t('company_tab') }}</th>
        <th v-if="this.currentUser.roleId === 1">{{ $t('actions') }}</th>
      </tr>
      </thead>
      <tbody class="tbody-container">
      <tr class="tr-class" v-if="!loading" v-for="(item, index) in items" :key="item.uuid || item.id || index">
        <td class="td-style"><input type="checkbox" @change="(e) => toggleItem(e, item.uuid || item.id)" :checked="selectedItemIds.includes(item.uuid || item.id)"></td>

        <td @click="viewDetails(item)" class="td-style">
          <div class="name-row">
              <img v-if="images" class="product-image" :src="item.images[0]" />
              <img v-else class="no-image-placeholder" src="../../assets/images/ibility-default.jpeg"/>
            <template v-if="item.title">
              <span class="product-title">{{ truncateText(item.title, 30) }}</span>
            </template>
            <template v-else>
              <em class="no-data-text">N/A</em>
            </template>
          </div>
        </td>

        <td class="td-style">
          <span class="status-badge" :class="item.isAccepted ? 'approved' : 'pending'">
            <span class="status-dot"></span>
            <span v-if="item.isAccepted">{{ $t('approved') }}</span>
            <span v-else>{{ $t('pending_approval') }}</span>
          </span>
        </td>

        <td class="td-style">
          <span class="date-info">
            <img src="../../assets/images/datepicker.svg">
            {{ formatDate(item.createdAt) }}
          </span>
        </td>

        <td class="td-style">
          <span class="price-info">
            {{ item.formattedPrice || item.price + ' ' + item.currency }}
          </span>
        </td>

        <td class="td-style">
          <template v-if="item.marketplaceCategory?.title">
            <span class="category-name">{{ truncateText(item.marketplaceCategory.title, 20) }}</span>
          </template>
          <template v-else>
            <em class="no-data-text">N/A</em>
          </template>
        </td>

        <td class="td-style">
          <img class="company-logo" :src="item.companyInfo?.logo">
          <span>{{item.companyInfo.name}}</span>
        </td>

        <td class="td-style" v-if="this.currentUser.roleId === 1">
          <div class="action-buttons">
            <button
              class="action-btn approve"
              @click="approveItem(item)"
              :disabled="item.isAccepted"
              v-if="!item.isAccepted"
            >
              <img src="../../assets/images/checkboxchecked-circle.svg">
            </button>
            <button
              class="action-btn reject"
              @click="rejectItem(item)"
            >
              <img src="../../assets/images/cross.svg">
            </button>
          </div>
        </td>
      </tr>

      <tr v-else>
        <td colspan="8" class="table-loading">
          <VueSpinner size="16" height="100%" color="#8c8c8c" />
          <span class="text">{{ $t('loading') }}</span>
        </td>
      </tr>

      <tr v-if="!loading && items.length === 0">
        <td colspan="8" class="table-loading">
          <span class="text">{{ $t('no_data_found') }}</span>
        </td>
      </tr>
      </tbody>
    </table>
  </div>

  <div class="bottom-actions" v-if="selectedItemIds.length > 0">
    <span class="selected-label">{{ $t('selected') }} {{ selectedItemIds.length }} {{ $t('item') }}</span>
    <button class="bulk-action-btn approve" @click="bulkApprove">
      {{ $t('approve_selected') }}
    </button>
    <button class="bulk-action-btn reject" @click="bulkReject">
      {{ $t('reject_selected') }}
    </button>
  </div>

  <RejectItemModal
    :visible="showRejectModal"
    :item="selectedItemForReject"
    @close="closeRejectModal"
    @confirm="handleRejectConfirm"
  />
</template>

<script>
import { VueSpinner } from "vue3-spinners";
import RejectItemModal from '../modals/RejectItemModal.vue';
import marketplaceService from '@/service/marketplaceService';
import { handleErrorResponse } from '@/utils/utils';

export default {
  name: 'PendingApprovalTable',
  components: {
    VueSpinner,
    RejectItemModal
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  watch: {
  },
  data() {
    return {
      selectAll: false,
      selectedItemIds: [],
      currentUser: null,
      showRejectModal: false,
      selectedItemForReject: null
    }
  },
  mounted() {
    const user = JSON.parse(localStorage.getItem('user'));
    this.currentUser = user;
  },
  methods: {
    viewDetails(item) {
      this.$router.push(`/marketplace/product/${item.uuid}`);
    },
    toggleAll() {
      this.selectAll = !this.selectAll;
      if (this.selectAll) {
        this.selectedItemIds = this.items.map(item => item.uuid || item.id).filter(id => id);
      } else {
        this.selectedItemIds = [];
      }
    },
    toggleItem(e, itemId) {
      if (!itemId) return;

      if (e.target.checked) {
        this.selectedItemIds.push(itemId);
      } else {
        this.selectedItemIds = this.selectedItemIds.filter(id => id !== itemId);
      }
    },
    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    async approveItem(item) {
      try {
        if (Array.isArray(item)) {
          // item bir UUID dizisi: ["uuid1", "uuid2", ...]
          const successIds = [];
          const failedIds = [];

          for (const itemId of item) {
            try {
              await marketplaceService.approveItem(itemId);
              successIds.push(itemId);
            } catch (err) {
              failedIds.push(itemId);
              console.error(`Failed to approve item ${itemId}:`, err);
            }
          }

          // Başarılı onaylamalar için mesaj
          if (successIds.length > 0) {
            this.$snackbar.add({
              text: `${successIds.length} ${this.$t('content')} ${this.$t('approved')}`,
              type: 'success'
            });
          }

          // Başarısız onaylamalar için uyarı
          if (failedIds.length > 0) {
            this.$snackbar.add({
              text: `${failedIds.length} ${this.$t('content')} ${this.$t('could_not_be_approved')}`,
              type: 'error'
            });
          }

        } else {
          // Tekil onaylama için (item bir obje)
          await marketplaceService.approveItem(item.uuid || item.id);
          this.$snackbar.add({
            text: `${item.title || this.$t('content')} ${this.$t('approved')}`,
            type: 'success'
          });
        }

        this.$emit('refresh-data');
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
    },
    rejectItem(item) {
      this.selectedItemForReject = item;
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedItemForReject = null;
    },
    async handleRejectConfirm({ item, rejectionReason }) {
      try {
        if (Array.isArray(item)) {
          // item bir UUID dizisi: ["uuid1", "uuid2", ...]
          const successIds = [];
          const failedIds = [];

          for (const itemId of item) {
            try {
              await marketplaceService.rejectItem(itemId, {
                rejectionReason: rejectionReason
              });
              successIds.push(itemId);
            } catch (err) {
              failedIds.push(itemId);
              console.error(`Failed to reject item ${itemId}:`, err);
            }
          }

          // Başarılı reddetmeler için mesaj
          if (successIds.length > 0) {
            this.$snackbar.add({
              text: `${successIds.length} ${this.$t('content')} ${this.$t('rejected')}`,
              type: 'success'
            });
          }

          // Başarısız reddetmeler için uyarı
          if (failedIds.length > 0) {
            this.$snackbar.add({
              text: `${failedIds.length} ${this.$t('content')} ${this.$t('could_not_be_rejected')}`,
              type: 'error'
            });
          }

        } else {
          // Tekil reddetme için (item bir obje)
          await marketplaceService.rejectItem(item.uuid || item.id, {
            rejectionReason: rejectionReason
          });
          this.$snackbar.add({
            text: `${item.title || this.$t('content')} ${this.$t('rejected')}`,
            type: 'success'
          });
        }

        this.closeRejectModal();
        this.$emit('refresh-data');
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.closeRejectModal();
      }
    },
    bulkApprove() {
      this.approveItem(this.selectedItemIds);
      this.$emit('bulk-approve', this.selectedItemIds);
      this.selectedItemIds = [];
      this.selectAll = false;
    },
    bulkReject() {
      this.rejectItem(this.selectedItemIds);
      this.$emit('bulk-reject', this.selectedItemIds);
      this.selectedItemIds = [];
      this.selectAll = false;
    }
  }
}
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.company-logo{
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
}

.content-table {
  width: 100%;
  border-collapse: collapse;
}

.header-table {
  background: #f8f9fa;
}

.trhead th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  color: #475467;
  border-bottom: 1px solid #eaecf0;
  text-transform: none !important;

}

.tbody-container tr {
  border-bottom: 1px solid #eaecf0;
}

.tbody-container tr:hover {
  background: #f9fafb;
}

.td-style {
  padding: 16px;
  vertical-align: middle;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e1e5e9;
}

.no-image-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e1e5e9;
}

.placeholder-icon {
  width: 20px;
  height: 20px;
  opacity: 0.5;
}

.product-title {
  font-weight: 500;
  color: #101828;
  font-size: 14px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.approved {
  background: #ddfbde;
  color: #040c2d;
}

.status-badge.pending {
  background: #fad9c6;
  color: #040c2d;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-badge.approved .status-dot {
  background: #3da33e;
}

.status-badge.pending .status-dot {
  background: #f09460;
}

.date-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #475467;
}

.date-info img {
  width: 16px;
  height: 16px;
}

.price-info {
  font-weight: 600;
  color: #101828;
  font-size: 14px;
}

.category-name, .company-name {
  color: #475467;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.approve {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.action-btn.approve:hover:not(:disabled) {
  background: #dcfce7;
}

.action-btn.approve:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.reject {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.action-btn.reject:hover {
  background: #fee2e2;
}

.bottom-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  margin-top: 8px;
  background: white;
  border-radius: 8px;
}

.selected-label {
  font-size: 14px;
  color: #6b7280;
}

.bulk-action-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.bulk-action-btn.approve {
  background: #16a34a;
  border-color: #16a34a;
  color: white;
}

.bulk-action-btn.reject {
  background: #dc2626;
  border-color: #dc2626;
  color: white;
}

.table-loading {
  text-align: center;
  padding: 40px 20px;
}

.table-loading .text {
  color: #667085;
  font-size: 14px;
  font-weight: 500;
}

.no-data-text {
  font-style: italic;
  color: #9ca3af;
  font-size: 12px;
}
</style>
